import { useMemo, useState, type CSSProperties } from 'react';
import { Avatar, Flex, Grid, Space, theme, Tooltip, Typography } from 'antd';
import { CardShell } from '@/components/cards/common';
import { VisibleFilterKey } from '@/components/cards/FilterCard/Filters';
import { useFilterContext, useDerivedNetworkContext } from '@/context';
import useNetworks from '@/hooks/useNetworks';
import type { BaseShow, SelectedNetwork } from '@/types';
import { twMerge } from 'tailwind-merge';
import {
  buildNetworksCountPrefix,
  buildShowsCountPrefix,
  useShowTitleList,
} from './ShowNamesLine';

type NetworkProfileCardEchoProps = {
  className?: string;
  visibleFilters?: VisibleFilterKey[];
};

const tooltipTextStyle = (token: ReturnType<typeof theme.useToken>['token']): CSSProperties => ({
  color: token.colorTextLightSolid,
  fontSize: token.fontSize,
  lineHeight: token.lineHeight,
});

const TooltipNameList = ({
  title,
  names,
}: {
  title: string;
  names: Array<{ id?: string; name?: string | null }>;
}) => {
  const { token } = theme.useToken();

  return (
    <Space orientation="vertical">
      <Typography.Text strong style={tooltipTextStyle(token)}>
        {title}
      </Typography.Text>
      <ul
        style={{
          margin: 0,
          paddingInlineStart: token.paddingLG,
          listStyleType: 'disc',
        }}
      >
        {names.map((item, index) => (
          <li key={item.id ?? `${item.name ?? 'item'}-${index}`}>
            <Typography.Text style={tooltipTextStyle(token)}>
              {(item.name ?? '').trim() || '—'}
            </Typography.Text>
          </li>
        ))}
      </ul>
    </Space>
  );
};

/**
 * Combined selection tooltip: full networks list, then full shows list.
 * Visibility is gated by the caller (only when a visible line is truncated).
 */
const CombinedSelectionTooltipContent = ({
  networks,
  shows,
}: {
  networks: SelectedNetwork[];
  shows: BaseShow[];
}) => {
  const { token } = theme.useToken();

  return (
    <Space orientation="vertical" size={token.marginSM}>
      {networks.length > 0 ? (
        <TooltipNameList title={buildNetworksCountPrefix(networks.length)} names={networks} />
      ) : null}
      {shows.length > 0 ? (
        <TooltipNameList title={buildShowsCountPrefix(shows.length)} names={shows} />
      ) : null}
    </Space>
  );
};

const titleEllipsisStyle = {
  margin: 0,
  flex: 1,
  minWidth: 0,
  maxWidth: '100%',
  overflow: 'hidden',
} as const;

const NetworkProfileCardEcho = ({ className, visibleFilters }: NetworkProfileCardEchoProps) => {
  const { token } = theme.useToken();
  const screens = Grid.useBreakpoint();
  const isSmallScreen = !screens.sm;
  const { filters } = useFilterContext();
  // `shows` is aggregated across every selected network (not `selectedNetwork.shows`).
  const { selectedNetwork, selectedNetworks, shows } = useDerivedNetworkContext();
  const { data: authorizedNetworks } = useNetworks();

  const networkCount = selectedNetworks.length;
  const networkNames = selectedNetworks.flatMap((network) => (network.name ? [network.name] : []));
  const isAllNetworks =
    networkCount > 1 &&
    Array.isArray(authorizedNetworks) &&
    networkCount === authorizedNetworks.length;
  const showMultiNetworkTitle = !isAllNetworks && networkCount > 1;

  const subtitleLineStyle: CSSProperties = {
    margin: 0,
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    fontSize: token.fontSizeSM,
    lineHeight: token.lineHeightSM,
    color: token.colorTextSecondary,
  };

  const showAvatarLargePx = token.sizeXXL * 2 - token.sizeSM; // 84px
  const showAvatarSmallPx = token.sizeXL;
  const showAvatarPx = isSmallScreen ? showAvatarSmallPx : showAvatarLargePx;

  /** Show-level branding when the page exposes a show filter, or when the network has only one show. */
  const useShowLevel =
    visibleFilters === undefined ||
    visibleFilters.includes(VisibleFilterKey.Shows) ||
    shows.length === 1;

  // Only honor selectedShows when the Shows filter is on this page — otherwise leftover filter
  // state from another page must not shrink the subtitle (matches non-Echo NetworkProfileCard).
  const showsFilterVisible =
    visibleFilters === undefined || visibleFilters.includes(VisibleFilterKey.Shows);

  // `[]` means all shows. Filter state only stores show ids — resolve against context shows.
  // Memoized so `useShowTitleList` keeps a stable `names` reference across renders.
  const selectedShowIds = filters?.selectedShows ?? [];
  const { displayedShows, showNames } = useMemo(() => {
    const resolvedShows =
      !showsFilterVisible || selectedShowIds.length === 0
        ? shows
        : selectedShowIds.flatMap((showId) => {
            const show = shows.find((networkShow) => networkShow.id === showId);
            return show ? [show] : [];
          });

    return {
      displayedShows: resolvedShows,
      showNames: resolvedShows.flatMap((show) => (show.name ? [show.name] : [])),
    };
  }, [selectedShowIds, shows, showsFilterVisible]);
  const showCount = displayedShows.length;

  const selectedShow =
    useShowLevel && networkCount <= 1 && displayedShows.length === 1
      ? displayedShows[0]
      : undefined;

  const selectedShowImageSrc =
    selectedShow && 'image_url' in selectedShow && selectedShow.image_url
      ? selectedShow.image_url
      : undefined;

  const {
    lineRef: showLineRef,
    countRef: showCountRef,
    description: showsDescription,
    isTruncated: isShowsListTruncated,
  } = useShowTitleList(showCount, showNames);

  const [isNetworkTitleEllipsis, setIsNetworkTitleEllipsis] = useState(false);

  // Open only when a visible line is truncated; omit networks section for a single-network selection.
  // Gate network ellipsis with `showMultiNetworkTitle` so stale state is ignored when not multi-network
  const shouldShowSelectionTooltip = (showMultiNetworkTitle && isNetworkTitleEllipsis) || isShowsListTruncated;
  const tooltipTitle = shouldShowSelectionTooltip ? (
    <CombinedSelectionTooltipContent
      networks={networkCount > 1 ? selectedNetworks : []}
      shows={displayedShows}
    />
  ) : undefined;

  const renderNetworkTitleText = isAllNetworks
    ? 'All Networks'
    : showMultiNetworkTitle
      ? networkNames.join(' • ')
      : (selectedNetwork.name ?? 'Network');

  const renderProfileSummary = (
    <Flex vertical flex={1} gap="small" style={titleEllipsisStyle}>
      <Typography.Title
        key={showMultiNetworkTitle ? 'multi-network' : 'network'}
        ellipsis={{
          rows: 1,
          tooltip: false,
          ...(showMultiNetworkTitle ? { onEllipsis: setIsNetworkTitleEllipsis } : {}),
        }}
        level={2}
        style={titleEllipsisStyle}
      >
        {renderNetworkTitleText}
      </Typography.Title>
      {showCount > 0 && (
        <Flex data-testid="network-shows-subtitle-row" align="center" gap="small">
          <Typography.Paragraph ref={showLineRef} style={subtitleLineStyle}>
            <strong ref={showCountRef}>{buildShowsCountPrefix(showCount)}</strong>
            {showsDescription ? `: ${showsDescription}` : ''}
          </Typography.Paragraph>
        </Flex>
      )}
    </Flex>
  );

  return (
    <CardShell type="secondary" className={twMerge('flex flex-col', className)}>
      <Flex>
        {selectedShow ? (
          <Flex
            align="center"
            gap={isSmallScreen ? 'small' : 'large'}
            style={titleEllipsisStyle}
          >
            {selectedShowImageSrc ? (
              <Avatar
                data-testid={isSmallScreen ? 'selected-show-image-sm' : 'selected-show-image'}
                draggable={false}
                shape="square"
                size={showAvatarPx}
                src={selectedShowImageSrc}
              />
            ) : null}
            <Typography.Title ellipsis={{ rows: 1 }} level={2} style={titleEllipsisStyle}>
              {selectedShow.name}
            </Typography.Title>
          </Flex>
        ) : (
          <Tooltip title={tooltipTitle} placement="topLeft">
            {renderProfileSummary}
          </Tooltip>
        )}
      </Flex>
    </CardShell>
  );
};

export default NetworkProfileCardEcho;
